import "./styles.css";
import { ShapefileRegistration } from "../../../components/SystemManager/Registrations/Shapefile";
import { ReactComponent as UsersIcon } from "../../../assets/svg/icons/users.svg";
import { ReactComponent as CalendarIcon } from "../../../assets/svg/icons/calendar.svg";
import { ReactComponent as PlusIcon } from "../../../assets/svg/icons/plus.svg";
import { ReactComponent as BackIcon } from "../../../assets/svg/icons/back.svg";
import { ViewCards } from "../../../components/ViewCards/Index";
import { useEditOnboarding } from "../../../hooks/useEditOnboarding";
import { CreateBatch } from "../../../components/SystemManager/Animals/Batches/CreateBatch";
import { CreateFarm } from "../../../components/SystemManager/Registrations/PropertyAreas/Farm";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useFilter } from "../../../hooks/useFilter";
import { MicroArea } from "../../../components/SystemManager/Registrations/PropertyAreas/MicroArea";
import { Area } from "../../../components/SystemManager/Registrations/PropertyAreas/Area";
import { Loading } from "../../../components/Loading/Index";
import { ViewTables } from "../../../components/SystemManager/Monitoring/Tables/Index";
import { CreateProducts } from "../../../components/SystemManager/MaterialTypes/Products";
import { ButtonOnlyTitle } from "../../../components/Buttons/ButtonOnlyTitle/Index";
import { CreateEvaluatorFormV2 as CreateEvaluator } from "../../../components/FormsV2";
import { ListEvaluators } from "../../../components/SystemManager/Monitoring/PastureEvaluation/ListEvaluators";
import StartEvaluation from "../../../components/SystemManager/Monitoring/PastureEvaluation/Modal/StartEvaluation";
import {
  IOptionsSideBarLinks,
  IOptionsSystemManagerLinks,
  optionsSideBar,
} from "../../../contexts/FilterContext";
import CreatePastureManagement from "../../../components/SystemManager/Animals/PastureManagement";
import ArchiveEvaluation from "../../../components/SystemManager/Monitoring/PastureEvaluation/Modal/ArchiveEvaluation";
import CancelateEvaluation from "../../../components/SystemManager/Monitoring/PastureEvaluation/Modal/CancelateEvaluation";
import { IOptionsEditOnboarding } from "../../../contexts/EditOnboardingContext";
import { Inventory } from "../../../components/Tasks/Inventory";
import { format } from "date-fns";
import { TableMonitorProvider } from "../../../contexts/TableMonitorContext";
import { ProfilesUsersV2 } from "../../../components/SystemManager/Registrations/Profiles/Index";
import { PastureEvaluationReportFormV2, SalterFormV2 } from "../../../components/FormsV2";
import { StockManagement } from "../../../components/SystemManager/Supplementation/Stock";
import { DropdownV2, OptionV2 } from "../../../components/FormControlsV2";
import { AlertModal } from "../../../components/Modal/Alert/Index";
import { TitleModal } from "../../../components/Modal/Contents/TitleModal/Index";
import { TextModal } from "../../../components/Modal/Contents/TextModal/Index";
import { CreateSalterUserModal } from "../../../components/SystemManager/Supplementation/Salters";
import { TaskRequestsPage } from "../../TaskRequests";
import { useTranslation } from "react-i18next";

const PAGE_TITLE_KEYS: Record<string, string> = {
  SHAPEFILE: "shapefile",
  INVENTORY: "titles.inventory", TASK_EVALUATION: "taskMonitoring", CREATE_PRODUCTS: "titles.createProduct",
  PRODUCTS: "products", STOCK: "stock", SALTERS: "salters", PASTURE_EVALUATION: "pastureEvaluation",
  MANAGER_EVALUATORS: "titles.manageEvaluators", PROPERTIES_AREAS: "propertiesAreas", PROFILES_USERS: "profiles",
  CREATE_BATCH: "batches", CATTLE_MAP: "categoriesWeights", PASTURE_MANAGEMENT: "pastureManagement",
  FAILURES_SYNC: "titles.syncFailures", SOLICITACOES: "titles.requests",
};

const FORM_TITLE_KEYS: Partial<
  Record<NonNullable<IOptionsEditOnboarding>, { create: string; edit: string }>
> = {
  farms: { create: "createFarm", edit: "editFarm" }, areas: { create: "createArea", edit: "editArea" },
  "micro-areas": { create: "createPaddock", edit: "editPaddock" }, batches: { create: "createBatch", edit: "viewBatch" },
  "create-products": { create: "../titles.createProduct", edit: "editProduct" },
  "pasture-management": { create: "createManagement", edit: "editManagement" },
  "create-evaluator": { create: "createEvaluator", edit: "editEvaluator" },
  "evaluation-report": { create: "downloadEvaluation", edit: "downloadEvaluation" },
  "batch-inventory": { create: "../titles.inventory", edit: "viewInventory" },
  salters: { create: "linkSalter", edit: "editSalter" },
};

const DEFAULT_FILTER_BY_SYSTEM_MANAGER: Partial<
  Record<NonNullable<IOptionsSystemManagerLinks>, IOptionsEditOnboarding>
> = {
  PRODUCTS: "create-products",
  PROPERTIES_AREAS: "farms",
  PASTURE_EVALUATION: "evaluations",
  CREATE_BATCH: "batches",
  CATTLE_MAP: "animal-category",
  PASTURE_MANAGEMENT: "pasture-management",
  SALTERS: "salters",
};

const TABLE_OPTIONS = new Set([
  "PRODUCTS",
  "TASK_EVALUATION",
  "FAILURES_SYNC",
  "PASTURE_EVALUATION",
  "PASTURE_MANAGEMENT",
]);
const CARD_OPTIONS = new Set([
  "PROPERTIES_AREAS",
  "SALTERS",
  "CREATE_BATCH",
  "CATTLE_MAP",
  "INVENTORY",
]);
const FORM_SIDE_BAR_OPTIONS = new Set(["MANAGER_SYSTEM"]);
const FORM_ADD_EXCEPTIONS: IOptionsEditOnboarding[] = [
  "animal-category",
  "weight-animals",
];
const OPTIONS_WITHOUT_ADD_BUTTON = new Set([
  "SHAPEFILE",
  "CATTLE_MAP",
  "PASTURE_EVALUATION",
  "TASK_EVALUATION",
  "PROFILES_USERS",
  "FAILURES_SYNC",
  "INVENTORY",
  "PASTURE_MANAGEMENT",
  "MANAGER_EVALUATORS",
  "STOCK",
  "SOLICITACOES",
]);
const OPTIONS_WITH_CALENDAR = new Set(["TASK_EVALUATION", "INVENTORY"]);

function getFormTitle(optionForm: IOptionsEditOnboarding, idSelected: number, t: (key: string) => string) {
  if (!optionForm) return "";
  const title = FORM_TITLE_KEYS[optionForm];
  if (!title) return PAGE_TITLE_KEYS[optionForm] ? t(`systemManager.${PAGE_TITLE_KEYS[optionForm]}`) : "";
  const key = idSelected ? title.edit : title.create;
  return t(key.startsWith("../") ? `systemManager.${key.slice(3)}` : `systemManager.forms.${key}`);
}

export function ShowData() {
  const { t } = useTranslation();
  const {
    filterSelected,
    setFilterSelected,
    idSelected,
    optionForm,
    setOptionForm,
    farmIdSelected,
    setFarmIdSelected,
  } = useEditOnboarding();
  const {
    systemManager,
    setSystemManager,
    sideBarOptionSelected,
    setModalCalendarFilter,
    setFocusLastDay,
    rangeDays,
    setSelectRangeDate,
    pastureManagementHistory,
    setPastureManagementHistory,
    responseFarms,
  } = useFilter();
  const [isMobile, setIsMobile] = useState(false);
  const [shapefileStepsContainer, setShapefileStepsContainer] = useState<HTMLDivElement | null>(null);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [hiddenBreadcrumbItems, setHiddenBreadcrumbItems] = useState(0);
  const [stockFarm, setStockFarm] = useState<OptionV2<number> | null>(null);
  const [stockHasUnsavedChanges, setStockHasUnsavedChanges] = useState(false);
  const [showCreateSalterUser, setShowCreateSalterUser] = useState(false);
  const [saltersReloadKey, setSaltersReloadKey] = useState(0);
  const [pendingStockFarm, setPendingStockFarm] = useState<OptionV2<number> | null>(null);
  const subtitleRef = useRef<HTMLSpanElement>(null);
  const subtitleMeasureRef = useRef<HTMLSpanElement>(null);
  const getMobileBreakpoint = useCallback(
    () => (isSidebarExpanded ? 1500 : 1280),
    [isSidebarExpanded],
  );

  const managerFarmOptions = useMemo<OptionV2<number>[]>(
    () => [...(responseFarms || [])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((farm) => ({ label: farm.name, value: Number(farm.id), info: farm })),
    [responseFarms],
  );

  const cattleMapFarm = useMemo(
    () => managerFarmOptions.find((option) => Number(option.value) === Number(farmIdSelected)) || null,
    [farmIdSelected, managerFarmOptions],
  );

  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= getMobileBreakpoint());
    };

    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, [getMobileBreakpoint]);

  useEffect(() => {
    const handleSidebarToggle = (event: CustomEvent<{ isOpen: boolean }>) => {
      setIsSidebarExpanded(event.detail.isOpen);
    };

    window.addEventListener("sidebarToggle", handleSidebarToggle as EventListener);
    return () => {
      window.removeEventListener("sidebarToggle", handleSidebarToggle as EventListener);
    };
  }, []);

  useEffect(() => {
    if (responseFarms?.length) {
      const sortedFarms = [...responseFarms].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      setFarmIdSelected(Number(sortedFarms[0].id));
    }
  }, [responseFarms, setFarmIdSelected]);

  const currentOption =
    sideBarOptionSelected == "MANAGER_SYSTEM"
      ? systemManager
      : sideBarOptionSelected;

  const currentTitle = currentOption && PAGE_TITLE_KEYS[currentOption] ? t(`systemManager.${PAGE_TITLE_KEYS[currentOption]}`) : "";
  const inventoryFarm = optionForm === "batch-inventory" ? cattleMapFarm : null;
  const formTitle = inventoryFarm
    ? inventoryFarm.label
    : optionForm ? getFormTitle(optionForm, idSelected, t) : "";
  const breadcrumbRoot =
    sideBarOptionSelected == "MANAGER_SYSTEM" ? t("systemManager.title") : "";
  const breadcrumbParent =
    currentOption == "MANAGER_EVALUATORS" ? t("systemManager.pastureEvaluation")
      : currentOption === "SHAPEFILE" ? t("systemManager.propertiesAreas") : "";
  const breadcrumbItems = [
    breadcrumbRoot,
    breadcrumbParent,
    currentTitle,
    formTitle,
  ].filter(Boolean);
  const breadcrumbSubtitleItems =
    breadcrumbItems.length > 1 ? breadcrumbItems.slice(0, -1) : [];
  const breadcrumbSubtitleKey = breadcrumbSubtitleItems.join("|");
  const visibleBreadcrumbStartIndex = Math.min(
    hiddenBreadcrumbItems,
    Math.max(breadcrumbSubtitleItems.length - 1, 0),
  );
  const visibleBreadcrumbItems = breadcrumbSubtitleItems.slice(
    visibleBreadcrumbStartIndex,
  );
  const breadcrumbTitle = breadcrumbItems[breadcrumbItems.length - 1] || "";
  const showBackButton =
    !!inventoryFarm || (
      !!currentOption &&
      !optionsSideBar.includes(currentOption as IOptionsSideBarLinks)
    );
  const hasAddException =
    !!filterSelected && FORM_ADD_EXCEPTIONS.includes(filterSelected);
  const showContextActions = !!currentOption && !optionForm && !hasAddException;

  const openCalendarStartDate = () => {
    setSelectRangeDate(2);
    setModalCalendarFilter(true);
    setFocusLastDay(false);
  };

  const openCalendarEndDate = () => {
    setSelectRangeDate(2);
    setModalCalendarFilter(true);
    if (rangeDays?.from?.toDateString() != rangeDays?.to?.toDateString()) {
      setFocusLastDay(true);
    } else {
      setFocusLastDay(false);
    }
  };

  const renderDateFilters = () => (
    <div className="tasks_start_end_container">
      {isMobile ? (
        <button
          type="button"
          className="tasks_date_filter__calendar-mobile"
          aria-label={t("dateFilter.filterAria")}
          onClick={openCalendarEndDate}
        >
          <CalendarIcon />
        </button>
      ) : (
        <>
          <div
            className="dataFilter__owner--container"
            style={{ border: "1px solid #e5e5e5", cursor: "pointer" }}
            onClick={openCalendarStartDate}
          >
            <small>{t("dateFilter.from")}</small>
            <div>
              <h6>
                {format(
                  rangeDays?.from ? new Date(rangeDays.from) : new Date(),
                  "dd/MM/yyyy",
                )}
              </h6>
              <CalendarIcon />
            </div>
          </div>

          <div
            className="dataFilter__owner--container"
            style={{ border: "1px solid #e5e5e5", cursor: "pointer" }}
            onClick={openCalendarEndDate}
          >
            <small>{t("dateFilter.to")}</small>
            <div>
              <h6>
                {format(
                  rangeDays?.to ? new Date(rangeDays.to) : new Date(),
                  "dd/MM/yyyy",
                )}
              </h6>
              <CalendarIcon />
            </div>
          </div>
        </>
      )}
    </div>
  );

  useLayoutEffect(() => {
    const subtitleElement = subtitleRef.current;
    const measureElement = subtitleMeasureRef.current;
    if (!subtitleElement || !measureElement) return;

    const calculateHiddenItems = () => {
      const availableWidth = subtitleElement.clientWidth;
      if (!availableWidth) return;

      const itemElements = Array.from(
        measureElement.querySelectorAll<HTMLSpanElement>(
          ".path__subtitle-item",
        ),
      );
      const itemWidths = itemElements.map((item) => item.offsetWidth);
      const totalWidth = itemWidths.reduce((total, width) => total + width, 0);

      if (totalWidth <= availableWidth) {
        setHiddenBreadcrumbItems(0);
        return;
      }

      const ellipsisWidth =
        measureElement.querySelector<HTMLSpanElement>(
          ".path__subtitle-ellipsis",
        )?.offsetWidth || 0;
      let visibleWidth = totalWidth;
      let nextHiddenItems = 0;

      while (nextHiddenItems < itemWidths.length - 1) {
        visibleWidth -= itemWidths[nextHiddenItems];
        nextHiddenItems += 1;

        if (visibleWidth + ellipsisWidth <= availableWidth) break;
      }

      setHiddenBreadcrumbItems(nextHiddenItems);
    };

    calculateHiddenItems();

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(calculateHiddenItems);
      resizeObserver.observe(subtitleElement);
      return () => resizeObserver.disconnect();
    }

    window.addEventListener("resize", calculateHiddenItems);
    return () => window.removeEventListener("resize", calculateHiddenItems);
  }, [breadcrumbSubtitleKey]);

  useEffect(() => {
    if (systemManager && DEFAULT_FILTER_BY_SYSTEM_MANAGER[systemManager]) {
      setFilterSelected(DEFAULT_FILTER_BY_SYSTEM_MANAGER[systemManager]);
    }
    if (sideBarOptionSelected == "INVENTORY")
      setFilterSelected("batch-inventory");
    const openingEvaluatorForm =
      systemManager === "MANAGER_EVALUATORS" &&
      optionForm === "create-evaluator";
    if (!FORM_SIDE_BAR_OPTIONS.has(sideBarOptionSelected) && !openingEvaluatorForm)
      setOptionForm(undefined);
  }, [setFilterSelected, setOptionForm, sideBarOptionSelected, systemManager]);

  useEffect(() => {
    if (systemManager !== "STOCK") {
      setStockFarm(null);
      setPendingStockFarm(null);
      setStockHasUnsavedChanges(false);
    }
  }, [systemManager]);

  useEffect(() => {
    if (systemManager !== "STOCK" || stockFarm || !responseFarms?.length) return;
    const firstFarm = [...responseFarms].sort((a, b) => a.name.localeCompare(b.name))[0];
    setStockFarm({
      label: firstFarm.name,
      value: Number(firstFarm.id),
      info: firstFarm,
    });
  }, [responseFarms, stockFarm, systemManager]);

  function handleStockFarmChange(option: OptionV2<number> | null) {
    if (String(option?.value) === String(stockFarm?.value)) return;
    if (stockHasUnsavedChanges && option) {
      setPendingStockFarm(option);
      return;
    }
    setStockFarm(option);
  }

  function confirmStockFarmChange() {
    if (!pendingStockFarm) return;
    setStockHasUnsavedChanges(false);
    setStockFarm(pendingStockFarm);
    setPendingStockFarm(null);
  }

  function handleBack() {
    if (optionForm) {
      setOptionForm(undefined);
      return;
    }

    if (currentOption == "MANAGER_EVALUATORS") {
      setSystemManager("PASTURE_EVALUATION");
      return;
    }

    if (currentOption === "SHAPEFILE") {
      setSystemManager("PROPERTIES_AREAS");
      return;
    }

    setSystemManager(null);
  }

  function handleAdd() {
    if (currentOption === "SALTERS") {
      setShowCreateSalterUser(true);
      return;
    }
    setOptionForm(filterSelected);
  }

  function handleBreadcrumbClick(index: number) {
    const isRootPath = !!breadcrumbRoot && index === 0;
    const isPastureEvaluationParent =
      currentOption == "MANAGER_EVALUATORS" &&
      !!breadcrumbParent &&
      index === 1;

    if (isRootPath) {
      setOptionForm(undefined);
      setSystemManager(null);
      return;
    }

    if (isPastureEvaluationParent) {
      setOptionForm(undefined);
      setSystemManager("PASTURE_EVALUATION");
      return;
    }

    if (currentOption === "SHAPEFILE" && breadcrumbParent && index === 1) {
      setOptionForm(undefined);
      setSystemManager("PROPERTIES_AREAS");
      return;
    }

    setOptionForm(undefined);
  }

  return (
    <>
      <div className="templateData">
        {/* Header */}
        {currentOption === "SOLICITACOES" ? (
          <h2 style={{ marginBottom: 10 }}>{t("systemManager.titles.requests")}</h2>
        ) : (
          <div className={`container__category${currentOption === "SHAPEFILE" ? " container__category--shapefile" : ""}`}>
          <div className="path__content">
            {showBackButton && (
              <button type="button" className="back__button" onClick={handleBack} aria-label={t("commonUi.back")}>
                <BackIcon />
              </button>
            )}

            <div className="path__container">
              {breadcrumbSubtitleItems.length > 0 && (
                <>
                  <span className="path__subtitle" ref={subtitleRef}>
                    {visibleBreadcrumbStartIndex > 0 && (
                      <span className="path__subtitle-ellipsis">...</span>
                    )}
                    {visibleBreadcrumbItems.map((item, index) => {
                      const originalIndex = visibleBreadcrumbStartIndex + index;
                      return (
                        <span
                          key={`${item}-${originalIndex}`}
                          className="path__subtitle-item"
                        >
                          {originalIndex > 0 && (
                            <span className="path__separator">{">"}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleBreadcrumbClick(originalIndex)}
                          >
                            {item}
                          </button>
                        </span>
                      );
                    })}
                  </span>
                  <span
                    className="path__subtitle path__subtitle--measure"
                    ref={subtitleMeasureRef}
                  >
                    <span className="path__subtitle-ellipsis">...</span>
                    {breadcrumbSubtitleItems.map((item, index) => (
                      <span
                        key={`${item}-${index}`}
                        className="path__subtitle-item"
                      >
                        {index > 0 && (
                          <span className="path__separator">{">"}</span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleBreadcrumbClick(index)}
                        >
                          {item}
                        </button>
                      </span>
                    ))}
                  </span>
                </>
              )}
              <h2 className="path__title">{breadcrumbTitle}</h2>
            </div>
          </div>

          <div
            className={`path__actions${currentOption === "SHAPEFILE" ? " path__actions--shapefile" : ""}`}
            ref={currentOption === "SHAPEFILE" ? setShapefileStepsContainer : undefined}
          >
            {currentOption === "PROPERTIES_AREAS" && !optionForm && (
              <ButtonOnlyTitle
                title={t("systemManager.shapefile")}
                theme="dark"
                style={{ width: 160 }}
                onClick={() => {
                  setOptionForm(undefined);
                  setSystemManager("SHAPEFILE");
                }}
              />
            )}
            {!isMobile && (currentOption === "STOCK" || currentOption === "CATTLE_MAP") && (
              <div className="system-manager__farm-action">
                <DropdownV2
                  name={currentOption === "STOCK" ? "stock-farm" : "cattle-map-farm"}
                  value={currentOption === "STOCK" ? stockFarm : cattleMapFarm}
                  options={managerFarmOptions}
                  placeholder={t("managementUi.selectFarm")}
                  searchable
                  menuMount="overlay"
                  emptyMessage={t("managementUi.noFarm")}
                  onChange={currentOption === "STOCK"
                    ? handleStockFarmChange
                    : (option) => option && setFarmIdSelected(Number(option.value))}
                />
              </div>
            )}

            {!optionForm &&
              currentOption &&
              !OPTIONS_WITHOUT_ADD_BUTTON.has(currentOption) && (
                <button className={"add__button"} onClick={handleAdd}>
                  <PlusIcon />
                </button>
              )}

            {currentOption == "PASTURE_MANAGEMENT" && isMobile && (
              <button
                className={
                  pastureManagementHistory
                    ? "add__button--disabled"
                    : "add__button"
                }
                onClick={handleAdd}
                disabled={pastureManagementHistory}
              >
                <PlusIcon />
              </button>
            )}

            {showContextActions && currentOption === "PASTURE_EVALUATION" && (
              <>
                {renderDateFilters()}
                <button
                  className={"add__button"}
                  onClick={() => {
                    setSystemManager("MANAGER_EVALUATORS");
                    setOptionForm(undefined);
                  }}
                >
                  <UsersIcon />
                </button>
              </>
            )}

            {showContextActions &&
              currentOption &&
              OPTIONS_WITH_CALENDAR.has(currentOption) &&
              renderDateFilters()}

            {showContextActions && currentOption == "PASTURE_MANAGEMENT" && (
              <>
                <ButtonOnlyTitle
                  title={t("systemManager.active")}
                  theme={pastureManagementHistory ? "light" : "dark"}
                  type="button"
                  style={{ width: 200 }}
                  onClick={() => {
                    if (pastureManagementHistory)
                      setPastureManagementHistory(false);
                  }}
                  hidden={isMobile}
                />
                <ButtonOnlyTitle
                  title={t("systemManager.inactive")}
                  theme={pastureManagementHistory ? "dark" : "light"}
                  type="button"
                  style={{ width: 200 }}
                  onClick={() => {
                    if (!pastureManagementHistory)
                      setPastureManagementHistory(true);
                  }}
                  hidden={isMobile}
                />

                <button
                  className={
                    pastureManagementHistory
                      ? "add__button--disabled"
                      : "add__button"
                  }
                  onClick={handleAdd}
                  disabled={pastureManagementHistory}
                  hidden={isMobile}
                >
                  <PlusIcon />
                </button>
              </>
            )}

            {!optionForm && currentOption == "MANAGER_EVALUATORS" && (
              <button
                className={"add__button"}
                onClick={() => {
                  setOptionForm("create-evaluator");
                  setSystemManager("MANAGER_EVALUATORS");
                }}
              >
                <PlusIcon />
              </button>
            )}
          </div>
          </div>
        )}
        {/* Header */}

        <div className="delimiter" style={{ marginBottom: 20 }} />

        {/* View */}
        {optionForm ? (
          <section className={optionForm ? "body__editOnboarding" : ""}>
            {optionForm == "farms" ? <CreateFarm /> : <></>}
            {optionForm == "areas" ? <Area /> : <></>}
            {optionForm == "micro-areas" ? <MicroArea /> : <></>}
            {optionForm == "batches" ? <CreateBatch /> : <></>}
            {optionForm == "create-products" ? <CreateProducts /> : <></>}
            {optionForm == "create-evaluator" ? <CreateEvaluator /> : <></>}
            {optionForm == "evaluation-report" ? (
              <PastureEvaluationReportFormV2 />
            ) : (
              <></>
            )}
            {optionForm == "pasture-management" ? (
              <CreatePastureManagement />
            ) : (
              <></>
            )}
            {optionForm == "batch-inventory" ? <Inventory /> : <></>}
            {optionForm == "salters" ? <SalterFormV2 /> : <></>}
          </section>
        ) : (
          currentOption && (
            <>
              {TABLE_OPTIONS.has(currentOption) && (
                <TableMonitorProvider>
                  <ViewTables />
                </TableMonitorProvider>
              )}
              {CARD_OPTIONS.has(currentOption) && (
                <ViewCards
                  key={currentOption === "SALTERS" ? saltersReloadKey : undefined}
                  mobileHeaderAction={isMobile && currentOption === "CATTLE_MAP" ? (
                    <DropdownV2
                      name="cattle-map-farm-mobile"
                      value={cattleMapFarm}
                      options={managerFarmOptions}
                      placeholder={t("managementUi.selectFarm")}
                      searchable
                      menuMount="overlay"
                      emptyMessage={t("managementUi.noFarm")}
                      onChange={(option) => option && setFarmIdSelected(Number(option.value))}
                    />
                  ) : undefined}
                />
              )}
              {currentOption === "PROFILES_USERS" && <ProfilesUsersV2 />}
              {currentOption === "SHAPEFILE" && <ShapefileRegistration stepsContainer={shapefileStepsContainer} />}
              {currentOption === "MANAGER_EVALUATORS" && <ListEvaluators />}
              {currentOption === "STOCK" && (
                <StockManagement
                  selectedFarm={stockFarm}
                  onDirtyChange={setStockHasUnsavedChanges}
                  mobileHeaderAction={isMobile ? (
                    <DropdownV2
                      name="stock-farm-mobile"
                      value={stockFarm}
                      options={managerFarmOptions}
                      placeholder={t("managementUi.selectFarm")}
                      searchable
                      menuMount="overlay"
                      emptyMessage={t("managementUi.noFarm")}
                      onChange={handleStockFarmChange}
                    />
                  ) : undefined}
                />
              )}
              {currentOption === "SOLICITACOES" && <TaskRequestsPage />}
            </>
          )
        )}
        {/* View */}
      </div>

      {/* Modals */}
      <StartEvaluation />
      <CreateSalterUserModal
        visible={showCreateSalterUser}
        onClose={() => setShowCreateSalterUser(false)}
        onCreated={() => setSaltersReloadKey((value) => value + 1)}
      />
      <ArchiveEvaluation />
      <CancelateEvaluation />
      <AlertModal
        visible={!!pendingStockFarm}
        contentStyle={{ width: 520, height: 260, borderRadius: 20, padding: 30 }}
      >
        <section className="stock-management__discard-modal">
          <div>
            <TitleModal>{t("managementUi.discardChanges")}</TitleModal>
            <br />
            <TextModal>
              {t("managementUi.unsavedFarmChanges")}
            </TextModal>
          </div>
          <div className="stock-management__discard-actions">
            <ButtonOnlyTitle
              title={t("managementUi.continueEditing")}
              theme="light"
              onClick={() => setPendingStockFarm(null)}
            />
            <ButtonOnlyTitle
              title={t("commonUi.discard")}
              theme="urgent"
              onClick={confirmStockFarmChange}
            />
          </div>
        </section>
      </AlertModal>
      {/* Modals */}
    </>
  );
}
